/**
 * Display Settings Component
 *
 * Component for managing display and animation settings.
 *
 * @since 1.0.0
 */

import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { Input } from '@redfoxwp/core-components/components/input';
import { LoadingSpinner } from '@redfoxwp/core-components/components/loading-spinner';
import { SettingWrapper } from '@redfoxwp/core-components/components/setting-wrapper';
import { Toggle } from '@redfoxwp/core-components/components/toggle';
import { __ } from '@wordpress/i18n';

import { useSettingsContext } from '../../../contexts/settings-context';

/**
 * Display Settings component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const DisplaySettings = (): JSX.Element => {
  const { settings, loading, isSaving, isResetting, updateSettings } = useSettingsContext();

  const handleToggleChange = (key: keyof typeof settings.display) => (checked: boolean) => {
    updateSettings('display', {
      ...settings?.display,
      [key]: checked,
    });
  };

  const handleInputChange =
    (key: keyof typeof settings.display) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        event.target.type === 'number' ? parseInt(event.target.value, 10) : event.target.value;
      updateSettings('display', {
        ...settings?.display,
        [key]: value,
      });
    };

  // Show loading spinner only on initial data load
  if (loading) {
    return (
      <Card
        styleProps={{
          padding: '30px',
        }}
      >
        <LoadingSpinner
          isLoading={true}
          variant='ring'
          size='medium'
          centered={true}
          showText={true}
          text={__('Loading settings...', 'redfox-added-to-cart-popup')}
        />
      </Card>
    );
  }

  // Disable inputs during save/reset operations
  const isDisabled = isSaving || isResetting;

  return (
    <Flex direction='column' gap='20px' fullWidth>
      <FlexItem>
        <SettingWrapper
          label={__('Auto-hide the popup', 'redfox-added-to-cart-popup')}
          description={__(
            'Automatically hide the popup after a few seconds.',
            'redfox-added-to-cart-popup'
          )}
          styleProps={{
            containerPadding: '30px',
          }}
        >
          <Toggle
            checked={settings?.display?.auto_hide ?? false}
            onChange={handleToggleChange('auto_hide')}
            size='medium'
            disabled={isDisabled}
          />
        </SettingWrapper>
      </FlexItem>

      {settings?.display?.auto_hide && (
        <FlexItem>
          <SettingWrapper
            label={__('Auto-hide delay', 'redfox-added-to-cart-popup')}
            description={__(
              'Time in milliseconds before the popup automatically hides.',
              'redfox-added-to-cart-popup'
            )}
            styleProps={{
              containerPadding: '30px',
              inputAreaMaxWidth: '360px',
            }}
          >
            <Input
              type='number'
              value={settings?.display?.auto_hide_delay ?? 3000}
              onChange={handleInputChange('auto_hide_delay')}
              min={1000}
              max={30000}
              step={500}
              size='medium'
              disabled={isDisabled}
            />
          </SettingWrapper>
        </FlexItem>
      )}

      <FlexItem>
        <SettingWrapper
          label={__('Max Width of Popup', 'redfox-added-to-cart-popup')}
          description={__('Maximum width of the popup in pixels.', 'redfox-added-to-cart-popup')}
          styleProps={{
            containerPadding: '30px',
            inputAreaMaxWidth: '360px',
          }}
        >
          <Input
            type='number'
            value={settings?.display?.max_width ?? 600}
            onChange={handleInputChange('max_width')}
            min={300}
            max={800}
            step={10}
            size='medium'
            disabled={isDisabled}
          />
        </SettingWrapper>
      </FlexItem>
    </Flex>
  );
};
